import { supabase } from './supabase'

// In dev, VITE_WORKER_URL is unset so requests go to /api (proxied by Vite → localhost:8787).
// In production, set VITE_WORKER_URL to your deployed worker URL in Cloudflare Pages env vars.
const API_BASE = import.meta.env.VITE_WORKER_URL ?? '/api'

interface FetchOptions extends RequestInit {
  signal?: AbortSignal
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  if (!token) throw new Error('Not authenticated')
  return { Authorization: `Bearer ${token}` }
}

export async function apiFetch<T>(
  path: string,
  options: FetchOptions = {}
): Promise<T> {
  const authHeaders = await getAuthHeaders()

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...options.headers,
    },
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }))
    throw Object.assign(new Error((body as { error?: string }).error ?? res.statusText), {
      status: res.status,
    })
  }

  return res.json() as Promise<T>
}

export async function apiStream(
  path: string,
  body: unknown,
  signal?: AbortSignal
): Promise<ReadableStreamDefaultReader<Uint8Array>> {
  const authHeaders = await getAuthHeaders()

  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
    },
    body: JSON.stringify(body),
    signal,
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText })) as { error?: string; reason?: string }
    const msg = err.reason ? `${err.error}: ${err.reason}` : (err.error ?? res.statusText)
    throw Object.assign(new Error(msg), { status: res.status })
  }

  if (!res.body) throw new Error('No response body')
  return res.body.getReader()
}
